import { FC, FormEventHandler, useEffect, useState } from "react";
import styles from "./edit-review.module.scss";
import { Modal } from "../../../../../ui/modal/Modal";
import { Button } from "../../../../../ui/button/Button";
import { z } from "zod";
import { Snackbar } from "../../../../../ui/snackbar/Snackbar";
import { useEditReviewMutation, useGetAllReviewsQuery } from "../reviewApi";
import { useGetAllQuery } from "../../airBase/airBaseApi";
import { IReviewList } from "../IReview";

type Props = {
  close: () => void;
  flight_work_id: number;
  review_id: number;
};

type State = {
  errors?: {
    airbase_id?: string[];
    multiplicity?: string[];
  };
  message?: string | null;
};

const FormSchema = z.object({
  airbase_id: z.coerce.number({
    message: "Выберите из списка",
  }),
  multiplicity: z.coerce.number().positive({
    message: "Кратность должна быть положительным числом",
  }),
});

const Form: FC<Props> = ({ close, flight_work_id, review_id }) => {
  const initialState: State = { message: null, errors: {} };
  const [state, setState] = useState<State>(initialState);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [current, setCurrent] = useState<IReviewList>();
  const [edit, { isError, isLoading, isSuccess }] = useEditReviewMutation();
  const {
    data: airBases,
    isError: isAirbaseError,
  } = useGetAllQuery();
  const {
    data,
    isError: isDataError,
  } = useGetAllReviewsQuery(Number(flight_work_id));

  const submit: FormEventHandler = (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    setState(initialState);
    setIsUXOpen(true);

    const validatedFields = FormSchema.safeParse({
      airbase_id: formData.get("airbase_id"),
      multiplicity: formData.get("multiplicity"),
    });

    if (!validatedFields.success) {
      setState({
        errors: validatedFields.error.flatten().fieldErrors,
        message: "Не удалось сохранить форму. Заполните все поля.",
      });
      return;
    }

    const { airbase_id, multiplicity } = validatedFields.data;

    edit({
      review_id,
      flight_work_id,
      airbase_id,
      multiplicity,
    });
  };

  useEffect(() => {
    if (!isSuccess) return;
    setTimeout(() => {
      close();
    }, 1500);
  }, [isSuccess]);

  useEffect(() => {
    if (!data) return;

    const review = data.find((entry) => entry.id === review_id);
    if (!review) return;
    setCurrent(review);
  }, [data, review_id]);

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.title}>Редактирование осмотра</div>
      <div className={styles.row}>
        <label htmlFor="name" className={styles.label}>
          Авиаотделение
        </label>
        {current && airBases && (
          <select
            className={styles.select}
            name="airbase_id"
            defaultValue={current.airbase_id}
          >
            <option className={styles.option}>Выберите</option>
            {airBases.map((item) => (
              <option key={item.id} className={styles.option} value={item.id}>
                {item.short_name}
              </option>
            ))}
          </select>
        )}
        <div id="airbase_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.airbase_id &&
            state.errors.airbase_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="multiplicity" className={styles.label}>
          Кратность
        </label>
        <input
          className={styles.input}
          name="multiplicity"
          placeholder="Кратность"
          type="number"
          step={1}
          defaultValue={current?.multiplicity}
        />
        <div id="multiplicity-error" aria-live="polite" aria-atomic="true">
          {state.errors?.multiplicity &&
            state.errors.multiplicity.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>

      <div className={styles.actions}>
        <Button type="reset" onClick={() => setState(initialState)}>
          Очистить
        </Button>
        <Button type="submit" isProcessing={isLoading} isDisabled={isLoading}>
          Сохранить
        </Button>
      </div>
      {state.message && isUXOpen && (
        <Snackbar
          isOpen={Boolean(state.message)}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          {state.message}
        </Snackbar>
      )}
      {isSuccess && (
        <Snackbar isOpen={isSuccess} className="success" onClose={close}>
          Изменения сохранены
        </Snackbar>
      )}
      {isError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка сохранения. Попробуйте позже
        </Snackbar>
      )}
      {isAirbaseError && (
        <Snackbar
          isOpen={isAirbaseError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Не удалось загрузить список авиаотделений
        </Snackbar>
      )}
      { isDataError &&         <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка загрузки данных. Попробуйте позже
        </Snackbar>}
    </form>
  );
};

export const EditReview: FC<Props> = ({ close, flight_work_id, review_id }) => {
  return (
    <Modal close={close}>
      <Form
        close={close}
        flight_work_id={flight_work_id}
        review_id={review_id}
      />
    </Modal>
  );
};
